import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts";
import axios from "axios";

import AnimatedCounter from "../../../components/AnimatedCounter/AnimatedCounter";
import ProgressRing from "../../../components/ProgressRing/ProgressRing";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";

import "./DettaglioOper.css";
import Lf23Loader from "../../../components/Lf23Loader";

/**
 * Componente DettaglioOper - Gestisce la visualizzazione della tabella "Dettaglio Ore Operatori"
 * e dei grafici "Operatori per Ore Straordinarie" e "% Ore Non Lavorate per Giustificativo"
 *
 * Props:
 * - unfilteredHrData: oggetto { all: [], filtered: [] } con dati grezzi dal backend
 * - loading: boolean per indicare lo stato di caricamento
 * - fromDate: string data inizio (formato yyyy-mm-dd)
 * - toDate: string data fine (formato yyyy-mm-dd)
 * - selectedCategory: string categoria selezionata
 * - filterOptions: oggetto con le opzioni dei filtri
 */
export const DettaglioOper = ({
    unfilteredHrData = { all: [], filtered: [] },
    loading = false,
    fromDate,
    toDate,
    selectedCategory = "",
    filterOptions = {}
}) => {
    const [selectedOperatori, setSelectedOperatori] = useState([]); // Operatori selezionati dal grafico straordinari
    const [selectedGiustificativi, setSelectedGiustificativi] = useState([]); // Giustificativi selezionati dal grafico
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [isFullscreenChartStraordinari, setIsFullscreenChartStraordinari] = useState(false);
    const [isFullscreenChartGiustificativi, setIsFullscreenChartGiustificativi] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 480px)").matches);
    const chartRef = useRef(null);
    const chartGiustificativiRef = useRef(null);
    const chartSectionRef = useRef(null);
    const tableSectionRef = useRef(null);
    const modifierKeyRef = useRef(false);
    const clickProcessingRef = useRef(false);
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // Assicurati che i dati siano sempre array
    const dataAll = useMemo(() => {
        return Array.isArray(unfilteredHrData?.all) ? unfilteredHrData.all : [];
    }, [unfilteredHrData]);

    const dataFiltered = useMemo(() => {
        return Array.isArray(unfilteredHrData?.filtered) ? unfilteredHrData.filtered : [];
    }, [unfilteredHrData]);

    // Verifica se i dati sono nel formato corretto per questa tab (oggetto con all e filtered)
    const hasValidDataFormat = unfilteredHrData &&
        typeof unfilteredHrData === 'object' &&
        !Array.isArray(unfilteredHrData) &&
        'all' in unfilteredHrData;

    // Stato di caricamento e dati - dichiarato qui per usarlo negli useEffect
    // Mostra loader se: sta caricando OPPURE i dati non sono nel formato corretto (es. array da altra tab)
    const isLoading = loading || !hasValidDataFormat;
    const isDataReady = !loading && hasValidDataFormat && dataAll.length > 0;
    const isEmpty = !loading && hasValidDataFormat && dataAll.length === 0;

    // Reset della selezione quando cambiano i dati dal backend
    useEffect(() => {
        setSelectedOperatori([]);
        setSelectedGiustificativi([]);
    }, [unfilteredHrData]);

    // Chiudi fullscreen con Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenTable(false);
                setIsFullscreenChartStraordinari(false);
                setIsFullscreenChartGiustificativi(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 480px)");
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    // Sincronizza l'altezza della tabella con quella dei grafici
    useEffect(() => {
        let rafId = null;
        let timeoutId = null;

        const syncHeight = () => {
            if (chartSectionRef.current && tableSectionRef.current) {
                const chartHeight = chartSectionRef.current.offsetHeight;
                if (chartHeight > 0) {
                    tableSectionRef.current.style.maxHeight = `${chartHeight}px`;
                }
            }
        };

        // Attendi un po' per il render completo dei grafici
        timeoutId = setTimeout(() => {
            syncHeight();
        }, 100);

        const resizeObserver = new ResizeObserver(() => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                syncHeight();
            });
        });

        if (chartSectionRef.current) {
            resizeObserver.observe(chartSectionRef.current);
        }

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            resizeObserver.disconnect();
        };
    }, [selectedOperatori, unfilteredHrData, isDataReady]);

    // Listener per tracciare Ctrl/Cmd key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.metaKey || e.ctrlKey) {
                modifierKeyRef.current = true;
            }
        };
        const handleKeyUp = (e) => {
            if (!e.metaKey && !e.ctrlKey) {
                modifierKeyRef.current = false;
            }
        };
        const handleBlur = () => {
            modifierKeyRef.current = false;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    /**
     * Formatta i numeri con separatore migliaia (.) e decimali (,)
     */
    const formatNumber = (num) => {
        return new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            useGrouping: true
        }).format(num);
    };

    /**
     * Trova gli operatori che hanno un determinato giustificativo
     */
    const operatoriPerGiustificativo = useMemo(() => {
        const map = {};
        dataAll.forEach(item => {
            const giust = item.giustificativo_cat;
            if (!giust || giust.trim() === '') return;
            if (!map[giust]) map[giust] = new Set();
            map[giust].add(item.anagrafica_operatore_id);
        });
        // Converti Set in Array
        Object.keys(map).forEach(k => { map[k] = Array.from(map[k]); });
        return map;
    }, [dataAll]);

    /**
     * Trova i giustificativi di un determinato operatore
     */
    const giustificativiPerOperatore = useMemo(() => {
        const map = {};
        dataAll.forEach(item => {
            const opId = item.anagrafica_operatore_id;
            const giust = item.giustificativo_cat;
            if (!giust || giust.trim() === '') return;
            if (!map[opId]) map[opId] = new Set();
            map[opId].add(giust);
        });
        // Converti Set in Array
        Object.keys(map).forEach(k => { map[k] = Array.from(map[k]); });
        return map;
    }, [dataAll]);

    /**
     * Dati filtrati in base alle selezioni incrociate
     * Se seleziono un giustificativo -> filtro per operatori che hanno quel giustificativo
     * Se seleziono un operatore -> filtro per giustificativi di quell'operatore
     */
    const crossFilteredDataAll = useMemo(() => {
        let filtered = dataAll;

        // Filtra per giustificativi selezionati
        if (selectedGiustificativi.length > 0) {
            // Trova tutti gli operatori che hanno almeno uno dei giustificativi selezionati
            const operatoriConGiustificativo = new Set();
            selectedGiustificativi.forEach(giust => {
                const ops = operatoriPerGiustificativo[giust] || [];
                ops.forEach(opId => operatoriConGiustificativo.add(opId));
            });
            filtered = filtered.filter(item => operatoriConGiustificativo.has(item.anagrafica_operatore_id));
        }

        // Filtra per operatori selezionati
        if (selectedOperatori.length > 0) {
            filtered = filtered.filter(item => selectedOperatori.includes(item.anagrafica_operatore_id));
        }

        return filtered;
    }, [dataAll, selectedGiustificativi, selectedOperatori, operatoriPerGiustificativo]);

    const crossFilteredDataFiltered = useMemo(() => {
        let filtered = dataFiltered;

        // Filtra per giustificativi selezionati
        if (selectedGiustificativi.length > 0) {
            const operatoriConGiustificativo = new Set();
            selectedGiustificativi.forEach(giust => {
                const ops = operatoriPerGiustificativo[giust] || [];
                ops.forEach(opId => operatoriConGiustificativo.add(opId));
            });
            filtered = filtered.filter(item => operatoriConGiustificativo.has(item.anagrafica_operatore_id));
        }

        // Filtra per operatori selezionati
        if (selectedOperatori.length > 0) {
            filtered = filtered.filter(item => selectedOperatori.includes(item.anagrafica_operatore_id));
        }

        return filtered;
    }, [dataFiltered, selectedGiustificativi, selectedOperatori, operatoriPerGiustificativo]);

    /**
     * Memoizza i dati aggregati per operatore per la TABELLA
     * Usa i dati filtrati incrociati
     */
    const operatoriDataTable = useMemo(() => {
        if (crossFilteredDataAll.length === 0) return [];

        const operatoriMap = {};

        crossFilteredDataAll.forEach(item => {
            const idOperatore = item.anagrafica_operatore_id;
            const nominativoOperatore = item.nominativo_operatore || `Operatore ${idOperatore}`;

            if (!operatoriMap[idOperatore]) {
                operatoriMap[idOperatore] = {
                    id: idOperatore,
                    operatore: nominativoOperatore,
                    oreLavorateMinuti: 0,
                    minutiNonLavorati: 0,
                    oreDaContrattoMinuti: 0
                };
            }

            operatoriMap[idOperatore].oreLavorateMinuti += item.min_lavorati || 0;
            operatoriMap[idOperatore].minutiNonLavorati += item.min_non_lavorati || 0;
            operatoriMap[idOperatore].oreDaContrattoMinuti += item.min_da_contratto || 0;
        });

        const operatoriArray = Object.values(operatoriMap).map(op => {
            const oreStraordinarieMinuti = op.oreLavorateMinuti + op.minutiNonLavorati - op.oreDaContrattoMinuti;

            const oreLavorate = Number((op.oreLavorateMinuti / 60).toFixed(1));
            const oreNonLavorate = Number((op.minutiNonLavorati / 60).toFixed(1));
            const oreDaContratto = Number((op.oreDaContrattoMinuti / 60).toFixed(1));
            const oreStraordinarie = Number((oreStraordinarieMinuti / 60).toFixed(1));

            const percOreNonLavorate = oreDaContratto === 0 ? 0 :
                Number(((oreNonLavorate / oreDaContratto) * 100).toFixed(1));
            const percOreStraordinarie = oreDaContratto === 0 ? 0 :
                Number(((oreStraordinarie / oreDaContratto) * 100).toFixed(1));

            return {
                id: op.id,
                operatore: op.operatore,
                oreLavorate,
                oreNonLavorate,
                percOreNonLavorate,
                oreDaContratto,
                oreStraordinarie,
                percOreStraordinarie
            };
        });

        return operatoriArray.sort((a, b) => a.operatore.localeCompare(b.operatore));
    }, [crossFilteredDataAll]);

    /**
     * Dati della tabella (già filtrati da operatoriDataTable)
     */
    const filteredTableData = operatoriDataTable;

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const sortedTableData = useMemo(() => {
        if (!sortConfig.key) return filteredTableData;
        return [...filteredTableData].sort((a, b) => {
            const raw_a = a[sortConfig.key] ?? '';
            const raw_b = b[sortConfig.key] ?? '';
            const num_a = parseFloat(raw_a);
            const num_b = parseFloat(raw_b);
            const cmp = !isNaN(num_a) && !isNaN(num_b)
                ? num_a - num_b
                : String(raw_a).localeCompare(String(raw_b));
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [filteredTableData, sortConfig]);

    /**
     * Calcola la STRUTTURA BASE del grafico "Operatori per Ore Straordinarie" (ordine fisso)
     * Usa i dati completi per determinare quali operatori mostrare e in che ordine
     */
    const chartBaseOperatori = useMemo(() => {
        if (dataFiltered.length === 0) return [];

        const operatoriMap = {};

        dataFiltered.forEach(item => {
            const idOperatore = item.anagrafica_operatore_id;
            const nominativoOperatore = item.nominativo_operatore || `Operatore ${idOperatore}`;

            if (!operatoriMap[idOperatore]) {
                operatoriMap[idOperatore] = {
                    id: idOperatore,
                    operatore: nominativoOperatore,
                    oreLavorateMinuti: 0,
                    minutiNonLavorati: 0,
                    oreDaContrattoMinuti: 0
                };
            }

            operatoriMap[idOperatore].oreLavorateMinuti += item.min_lavorati || 0;
            operatoriMap[idOperatore].minutiNonLavorati += item.min_non_lavorati || 0;
            operatoriMap[idOperatore].oreDaContrattoMinuti += item.min_da_contratto || 0;
        });

        return Object.values(operatoriMap)
            .map(op => {
                const oreStraordinarieMinuti = op.oreLavorateMinuti + op.minutiNonLavorati - op.oreDaContrattoMinuti;
                const oreDaContratto = op.oreDaContrattoMinuti / 60;
                const oreNonLavorate = op.minutiNonLavorati / 60;

                if (oreDaContratto <= 0 && oreNonLavorate <= 0) {
                    return null;
                }

                const oreStraordinarie = Number((oreStraordinarieMinuti / 60).toFixed(1));

                return {
                    id: op.id,
                    operatore: op.operatore,
                    oreStraordinarie
                };
            })
            .filter(op => op !== null && op.oreStraordinarie > 0)
            .sort((a, b) => b.oreStraordinarie - a.oreStraordinarie)
            .slice(0, 20);
    }, [dataFiltered]);

    /**
     * Calcola i VALORI FILTRATI per il grafico straordinari
     * Filtra per giustificativi selezionati (gli operatori selezionati non filtrano questo grafico)
     */
    const chartDataStraordinari = useMemo(() => {
        if (chartBaseOperatori.length === 0) return { operatori: [], operatoriIds: [], oreStraordinarie: [], oreStraordinarieFiltrate: [] };

        // Se ci sono giustificativi selezionati, ricalcola i valori filtrati
        let dataToUse = dataFiltered;
        if (selectedGiustificativi.length > 0) {
            // Filtra solo i record con i giustificativi selezionati
            const operatoriConGiustificativo = new Set();
            selectedGiustificativi.forEach(giust => {
                const ops = operatoriPerGiustificativo[giust] || [];
                ops.forEach(opId => operatoriConGiustificativo.add(opId));
            });
            dataToUse = dataFiltered.filter(item => operatoriConGiustificativo.has(item.anagrafica_operatore_id));
        }

        // Calcola valori filtrati per ogni operatore della struttura base
        const operatoriMapFiltrato = {};
        dataToUse.forEach(item => {
            const idOperatore = item.anagrafica_operatore_id;
            if (!operatoriMapFiltrato[idOperatore]) {
                operatoriMapFiltrato[idOperatore] = {
                    oreLavorateMinuti: 0,
                    minutiNonLavorati: 0,
                    oreDaContrattoMinuti: 0
                };
            }
            operatoriMapFiltrato[idOperatore].oreLavorateMinuti += item.min_lavorati || 0;
            operatoriMapFiltrato[idOperatore].minutiNonLavorati += item.min_non_lavorati || 0;
            operatoriMapFiltrato[idOperatore].oreDaContrattoMinuti += item.min_da_contratto || 0;
        });

        const oreStraordinarieFiltrate = chartBaseOperatori.map(op => {
            const filtered = operatoriMapFiltrato[op.id];
            if (!filtered) return 0;
            const oreStraordinarieMinuti = filtered.oreLavorateMinuti + filtered.minutiNonLavorati - filtered.oreDaContrattoMinuti;
            return Number((oreStraordinarieMinuti / 60).toFixed(1));
        });

        return {
            operatori: chartBaseOperatori.map(op => op.operatore),
            operatoriIds: chartBaseOperatori.map(op => op.id),
            oreStraordinarie: chartBaseOperatori.map(op => op.oreStraordinarie),
            oreStraordinarieFiltrate
        };
    }, [chartBaseOperatori, dataFiltered, selectedGiustificativi, operatoriPerGiustificativo]);

    /**
     * Calcola la STRUTTURA BASE del grafico "% Ore Non Lavorate per Giustificativo" (ordine fisso)
     */
    const chartBaseGiustificativi = useMemo(() => {
        if (dataAll.length === 0) return [];

        const giustificativiMap = {};
        let totaleOreDaContrattoMinuti = 0;

        dataAll.forEach(item => {
            const giustificativo = item.giustificativo_cat;
            const minNonLavorati = item.min_non_lavorati || 0;
            const minDaContratto = item.min_da_contratto || 0;

            totaleOreDaContrattoMinuti += minDaContratto;

            if (!giustificativo || giustificativo.trim() === '') {
                return;
            }

            if (!giustificativiMap[giustificativo]) {
                giustificativiMap[giustificativo] = {
                    giustificativo: giustificativo,
                    minutiNonLavorati: 0
                };
            }

            giustificativiMap[giustificativo].minutiNonLavorati += minNonLavorati;
        });

        return Object.values(giustificativiMap)
            .map(giust => {
                const percOreNonLavorate = totaleOreDaContrattoMinuti === 0 ? 0 :
                    Number(((giust.minutiNonLavorati / totaleOreDaContrattoMinuti) * 100).toFixed(1));

                return {
                    giustificativo: giust.giustificativo,
                    percOreNonLavorate,
                    totaleOreDaContrattoMinuti
                };
            })
            .filter(giust => giust.percOreNonLavorate > 0)
            .sort((a, b) => b.percOreNonLavorate - a.percOreNonLavorate);
    }, [dataAll]);

    /**
     * Calcola i VALORI FILTRATI per il grafico giustificativi
     * Filtra per operatori selezionati (i giustificativi selezionati non filtrano questo grafico)
     */
    const chartDataGiustificativi = useMemo(() => {
        if (chartBaseGiustificativi.length === 0) return { giustificativi: [], percentuali: [], percentualiFiltrate: [] };

        // Se ci sono operatori selezionati, ricalcola i valori filtrati
        let dataToUse = dataAll;
        if (selectedOperatori.length > 0) {
            dataToUse = dataAll.filter(item => selectedOperatori.includes(item.anagrafica_operatore_id));
        }

        // Calcola valori filtrati
        const giustificativiMapFiltrato = {};
        let totaleOreDaContrattoMinutiFiltrato = 0;

        dataToUse.forEach(item => {
            const giustificativo = item.giustificativo_cat;
            const minNonLavorati = item.min_non_lavorati || 0;
            const minDaContratto = item.min_da_contratto || 0;

            totaleOreDaContrattoMinutiFiltrato += minDaContratto;

            if (!giustificativo || giustificativo.trim() === '') {
                return;
            }

            if (!giustificativiMapFiltrato[giustificativo]) {
                giustificativiMapFiltrato[giustificativo] = { minutiNonLavorati: 0 };
            }

            giustificativiMapFiltrato[giustificativo].minutiNonLavorati += minNonLavorati;
        });

        const percentualiFiltrate = chartBaseGiustificativi.map(giust => {
            const filtered = giustificativiMapFiltrato[giust.giustificativo];
            if (!filtered || totaleOreDaContrattoMinutiFiltrato === 0) return 0;
            return Number(((filtered.minutiNonLavorati / totaleOreDaContrattoMinutiFiltrato) * 100).toFixed(1));
        });

        return {
            giustificativi: chartBaseGiustificativi.map(g => g.giustificativo),
            percentuali: chartBaseGiustificativi.map(g => g.percOreNonLavorate),
            percentualiFiltrate
        };
    }, [chartBaseGiustificativi, dataAll, selectedOperatori]);

    /**
     * Calcola i totali per la tabella
     */
    const totaliTable = useMemo(() => {
        if (filteredTableData.length === 0) {
            return {
                oreLavorate: 0,
                oreNonLavorate: 0,
                percOreNonLavorate: 0,
                oreDaContratto: 0,
                oreStraordinarie: 0,
                percOreStraordinarie: 0
            };
        }

        const totOreLavorate = filteredTableData.reduce((sum, op) => sum + op.oreLavorate, 0);
        const totOreNonLavorate = filteredTableData.reduce((sum, op) => sum + op.oreNonLavorate, 0);
        const totOreDaContratto = filteredTableData.reduce((sum, op) => sum + op.oreDaContratto, 0);
        const totOreStraordinarie = filteredTableData.reduce((sum, op) => sum + op.oreStraordinarie, 0);

        const percOreNonLavorate = totOreDaContratto === 0 ? 0 :
            Number(((totOreNonLavorate / totOreDaContratto) * 100).toFixed(1));
        const percOreStraordinarie = totOreDaContratto === 0 ? 0 :
            Number(((totOreStraordinarie / totOreDaContratto) * 100).toFixed(1));

        return {
            oreLavorate: Number(totOreLavorate.toFixed(1)),
            oreNonLavorate: Number(totOreNonLavorate.toFixed(1)),
            percOreNonLavorate,
            oreDaContratto: Number(totOreDaContratto.toFixed(1)),
            oreStraordinarie: Number(totOreStraordinarie.toFixed(1)),
            percOreStraordinarie
        };
    }, [filteredTableData]);

    /**
     * Calcola i KPI riassuntivi per le summary cards
     * Usa crossFilteredDataAll per reagire alle selezioni dei grafici
     */
    const kpiSummary = useMemo(() => {
        if (crossFilteredDataAll.length === 0) {
            return {
                numOperatori: 0,
                oreLavorate: 0,
                oreNonLavorate: 0,
                oreDaContratto: 0,
                oreStraordinarie: 0,
                percAssenteismo: 0,
                mediaOreOperatore: 0
            };
        }

        // Operatori unici
        const operatoriSet = new Set(crossFilteredDataAll.map(d => d.anagrafica_operatore_id).filter(Boolean));
        const numOperatori = operatoriSet.size;

        // Totali minuti
        const totMinLavorati = crossFilteredDataAll.reduce((sum, d) => sum + (d.min_lavorati || 0), 0);
        const totMinNonLavorati = crossFilteredDataAll.reduce((sum, d) => sum + (d.min_non_lavorati || 0), 0);
        const totMinDaContratto = crossFilteredDataAll.reduce((sum, d) => sum + (d.min_da_contratto || 0), 0);

        // Converti in ore
        const oreLavorate = Number((totMinLavorati / 60).toFixed(1));
        const oreNonLavorate = Number((totMinNonLavorati / 60).toFixed(1));
        const oreDaContratto = Number((totMinDaContratto / 60).toFixed(1));
        const oreStraordinarie = Number(((totMinLavorati + totMinNonLavorati - totMinDaContratto) / 60).toFixed(1));

        // Percentuale assenteismo
        const percAssenteismo = oreDaContratto > 0 ? Number(((oreNonLavorate / oreDaContratto) * 100).toFixed(1)) : 0;

        // Media ore per operatore
        const mediaOreOperatore = numOperatori > 0 ? Number((oreLavorate / numOperatori).toFixed(1)) : 0;

        return {
            numOperatori,
            oreLavorate,
            oreNonLavorate,
            oreDaContratto,
            oreStraordinarie,
            percAssenteismo,
            mediaOreOperatore
        };
    }, [crossFilteredDataAll]);

    /**
     * Handler per il click sulle barre del grafico straordinari
     */
    const handleChartClick = useCallback((params) => {
        if (!params || params.componentType !== 'series') return;

        if (clickProcessingRef.current) return;
        clickProcessingRef.current = true;
        setTimeout(() => { clickProcessingRef.current = false; }, 100);

        const clickedIndex = params.dataIndex;
        const reversedIds = chartDataStraordinari.operatoriIds.slice().reverse();
        const operatoreId = reversedIds[clickedIndex];

        if (!operatoreId) return;

        const isMultiSelect = modifierKeyRef.current;

        setSelectedOperatori(prev => {
            if (isMultiSelect) {
                if (prev.includes(operatoreId)) {
                    return prev.filter(id => id !== operatoreId);
                } else {
                    return [...prev, operatoreId];
                }
            } else {
                if (prev.length === 1 && prev[0] === operatoreId) {
                    return [];
                }
                return [operatoreId];
            }
        });
    }, [chartDataStraordinari.operatoriIds]);

    /**
     * Handler per il click sulle barre del grafico giustificativi
     */
    const handleGiustificativoClick = useCallback((params) => {
        if (!params || params.componentType !== 'series') return;

        if (clickProcessingRef.current) return;
        clickProcessingRef.current = true;
        setTimeout(() => { clickProcessingRef.current = false; }, 100);

        const clickedIndex = params.dataIndex;
        const giustificativo = chartDataGiustificativi.giustificativi[clickedIndex];

        if (!giustificativo) return;

        const isMultiSelect = modifierKeyRef.current;

        setSelectedGiustificativi(prev => {
            if (isMultiSelect) {
                if (prev.includes(giustificativo)) {
                    return prev.filter(g => g !== giustificativo);
                } else {
                    return [...prev, giustificativo];
                }
            } else {
                if (prev.length === 1 && prev[0] === giustificativo) {
                    return [];
                }
                return [giustificativo];
            }
        });
    }, [chartDataGiustificativi.giustificativi]);

    /**
     * Resetta la selezione degli operatori
     */
    const handleClearSelectionOperatori = useCallback(() => {
        setSelectedOperatori([]);
    }, []);

    /**
     * Resetta la selezione dei giustificativi
     */
    const handleClearSelectionGiustificativi = useCallback(() => {
        setSelectedGiustificativi([]);
    }, []);

    /**
     * Resetta tutte le selezioni
     */
    const handleClearAllSelections = useCallback(() => {
        setSelectedOperatori([]);
        setSelectedGiustificativi([]);
    }, []);

    /**
     * Configurazione del grafico a barre orizzontali (Straordinari)
     */
    const getChartOptionsStraordinari = useCallback(() => {
        const { operatori, operatoriIds, oreStraordinarie, oreStraordinarieFiltrate } = chartDataStraordinari;

        const reversedOperatori = operatori.slice().reverse();
        const reversedIds = operatoriIds.slice().reverse();
        const reversedOreStraordinarie = oreStraordinarie.slice().reverse();
        const reversedOreStraordinarieFiltrate = oreStraordinarieFiltrate.slice().reverse();

        // Usa i valori filtrati se ci sono giustificativi selezionati
        const hasGiustificativiFilter = selectedGiustificativi.length > 0;
        const valoriDaUsare = hasGiustificativiFilter ? reversedOreStraordinarieFiltrate : reversedOreStraordinarie;

        // Calcola altezza dinamica
        const barHeight = 25;
        const minHeight = 300;
        const calculatedHeight = Math.max(minHeight, reversedOperatori.length * barHeight + 80);

        const selectedColor = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: 'rgba(220, 38, 38, 0.85)' },
            { offset: 0.5, color: 'rgba(185, 28, 28, 0.9)' },
            { offset: 1, color: 'rgba(153, 27, 27, 1)' }
        ]);

        const normalColor = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: 'rgba(239, 68, 68, 0.6)' },
            { offset: 0.5, color: 'rgba(220, 38, 38, 0.75)' },
            { offset: 1, color: 'rgba(185, 28, 28, 0.85)' }
        ]);

        const dimmedColor = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: 'rgba(239, 68, 68, 0.25)' },
            { offset: 0.5, color: 'rgba(220, 38, 38, 0.35)' },
            { offset: 1, color: 'rgba(185, 28, 28, 0.45)' }
        ]);

        // Calcola gli operatori evidenziati (selezionati direttamente O che hanno un giustificativo selezionato)
        const operatoriEvidenziati = new Set(selectedOperatori);
        if (selectedGiustificativi.length > 0) {
            selectedGiustificativi.forEach(giust => {
                const ops = operatoriPerGiustificativo[giust] || [];
                ops.forEach(opId => operatoriEvidenziati.add(opId));
            });
        }

        const hasAnySelection = selectedOperatori.length > 0 || selectedGiustificativi.length > 0;

        const dataWithStyles = valoriDaUsare.map((value, index) => {
            const operatoreId = reversedIds[index];
            const isHighlighted = operatoriEvidenziati.has(operatoreId);

            let itemColor;
            let labelColor;

            if (hasAnySelection) {
                if (isHighlighted) {
                    itemColor = selectedColor;
                    labelColor = '#991b1b';
                } else {
                    itemColor = dimmedColor;
                    labelColor = '#9ca3af';
                }
            } else {
                itemColor = normalColor;
                labelColor = '#dc2626';
            }

            return {
                value,
                itemStyle: {
                    color: itemColor,
                    borderRadius: [0, 4, 4, 0]
                },
                label: {
                    show: true,
                    position: 'right',
                    formatter: (params) => `${formatNumber(params.value)} h`,
                    color: labelColor,
                    fontWeight: 'bold',
                    fontSize: 10
                }
            };
        });

        return {
            height: calculatedHeight,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: [12, 16],
                textStyle: {
                    color: '#334155',
                    fontSize: 12
                },
                extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border-radius: 8px;',
                formatter: function (params) {
                    let result = `<div style="font-weight: 600; margin-bottom: 8px; font-size: 13px; color: #1e293b;">${params[0].name}</div>`;
                    result += `<div style="display: flex; justify-content: space-between; margin: 4px 0;">`;
                    result += `${params[0].marker} <span style="flex: 1; margin-left: 8px;">Ore Straordinari</span>`;
                    result += `<span style="font-weight: 600; margin-left: 16px; color: #dc2626;">${formatNumber(params[0].value)} h</span></div>`;
                    if (!hasAnySelection) {
                        result += '<div style="margin-top: 8px; font-size: 10px; color: #94a3b8;">Click per filtrare • Cmd/Ctrl+Click per multi-selezione</div>';
                    }
                    return result;
                }
            },
            grid: {
                left: '3%',
                right: '12%',
                bottom: '3%',
                top: '10',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                name: 'Ore',
                axisLabel: {
                    fontSize: 10,
                    formatter: (value) => formatNumber(value)
                }
            },
            yAxis: {
                type: 'category',
                data: reversedOperatori,
                axisLabel: {
                    fontSize: 10,
                    width: 120,
                    overflow: 'truncate',
                    ellipsis: '...'
                }
            },
            series: [
                {
                    name: 'Ore Straordinari',
                    type: 'bar',
                    data: dataWithStyles,
                    barMaxWidth: 20,
                    emphasis: {
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: 'rgba(185, 28, 28, 0.9)' },
                                { offset: 0.5, color: 'rgba(220, 38, 38, 0.95)' },
                                { offset: 1, color: 'rgba(153, 27, 27, 1)' }
                            ])
                        }
                    },
                    cursor: 'pointer'
                }
            ],
            animationDuration: 800,
            animationEasing: 'cubicOut',
            animationDelay: function (idx) {
                return idx * 30;
            }
        };
    }, [chartDataStraordinari, selectedOperatori, selectedGiustificativi, operatoriPerGiustificativo]);

    /**
     * Configurazione del grafico a barre verticali (Giustificativi)
     */
    const getChartOptionsGiustificativi = useCallback(() => {
        const { giustificativi, percentuali, percentualiFiltrate } = chartDataGiustificativi;

        // Usa i valori filtrati se ci sono operatori selezionati
        const hasOperatoriFilter = selectedOperatori.length > 0;
        const valoriDaUsare = hasOperatoriFilter ? percentualiFiltrate : percentuali;

        const selectedColor = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(37, 99, 235, 1)' },
            { offset: 1, color: 'rgba(29, 78, 216, 0.9)' }
        ]);

        const normalColor = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(59, 130, 246, 0.9)' },
            { offset: 1, color: 'rgba(37, 99, 235, 0.7)' }
        ]);

        const dimmedColor = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(59, 130, 246, 0.35)' },
            { offset: 1, color: 'rgba(37, 99, 235, 0.25)' }
        ]);

        // Calcola i giustificativi evidenziati (selezionati direttamente O che appartengono a un operatore selezionato)
        const giustificativiEvidenziati = new Set(selectedGiustificativi);
        if (selectedOperatori.length > 0) {
            selectedOperatori.forEach(opId => {
                const giusts = giustificativiPerOperatore[opId] || [];
                giusts.forEach(g => giustificativiEvidenziati.add(g));
            });
        }

        const hasAnySelection = selectedOperatori.length > 0 || selectedGiustificativi.length > 0;

        const dataWithStyles = valoriDaUsare.map((value, index) => {
            const giust = giustificativi[index];
            const isHighlighted = giustificativiEvidenziati.has(giust);

            let itemColor;
            let labelColor;

            if (hasAnySelection) {
                if (isHighlighted) {
                    itemColor = selectedColor;
                    labelColor = '#1e40af';
                } else {
                    itemColor = dimmedColor;
                    labelColor = '#9ca3af';
                }
            } else {
                itemColor = normalColor;
                labelColor = '#374151';
            }

            return {
                value,
                itemStyle: {
                    color: itemColor,
                    borderRadius: [4, 4, 0, 0]
                },
                label: {
                    show: true,
                    position: 'top',
                    formatter: (params) => params.value > 0 ? `${formatNumber(params.value)}%` : '',
                    color: labelColor,
                    fontWeight: 'bold',
                    fontSize: 10
                }
            };
        });

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: [12, 16],
                textStyle: {
                    color: '#334155',
                    fontSize: 12
                },
                extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border-radius: 8px;',
                formatter: function (params) {
                    let result = `<div style="font-weight: 600; margin-bottom: 8px; font-size: 13px; color: #1e293b;">${params[0].name}</div>`;
                    result += `<div style="display: flex; justify-content: space-between; margin: 4px 0;">`;
                    result += `${params[0].marker} <span style="flex: 1; margin-left: 8px;">% Ore Non Lavorate</span>`;
                    result += `<span style="font-weight: 600; margin-left: 16px; color: #2563eb;">${formatNumber(params[0].value)}%</span></div>`;
                    if (!hasAnySelection) {
                        result += '<div style="margin-top: 8px; font-size: 10px; color: #94a3b8;">Click per filtrare • Cmd/Ctrl+Click per multi-selezione</div>';
                    }
                    return result;
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: isMobile ? '5%' : '15%',
                top: '10',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: giustificativi,
                axisLabel: {
                    interval: 0,
                    rotate: isMobile ? 35 : 45,
                    fontSize: isMobile ? 9 : 10,
                    formatter: (value) => {
                        if (!value) return '';
                        return value.length > 15 ? value.substring(0, 15) + '...' : value;
                    }
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    fontSize: 10,
                    formatter: '{value}%'
                }
            },
            series: [
                {
                    name: '% Ore Non Lavorate',
                    type: 'bar',
                    data: dataWithStyles,
                    barMaxWidth: 40,
                    emphasis: {
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(37, 99, 235, 1)' },
                                { offset: 1, color: 'rgba(29, 78, 216, 0.85)' }
                            ])
                        }
                    },
                    cursor: 'pointer'
                }
            ],
            animationDuration: 800,
            animationEasing: 'cubicOut',
            animationDelay: function (idx) {
                return idx * 50;
            }
        };
    }, [chartDataGiustificativi, selectedGiustificativi, selectedOperatori, giustificativiPerOperatore]);

    /* Funzione per effettuare il download del file tabella */
    const handleDownloadFile = async () => {
        try {
            const dataToExport = {
                headers: ['Operatore', 'Ore Lavorate', 'Ore Non Lavorate', '% Ore Non Lav', 'Ore da Contratto', 'Ore Straordinarie', '% Ore Straordinarie'],
                rows: sortedTableData.map(op => [
                    op.operatore,
                    op.oreLavorate,
                    op.oreNonLavorate,
                    `${op.percOreNonLavorate}%`,
                    op.oreDaContratto,
                    op.oreStraordinarie,
                    `${op.percOreStraordinarie}%`
                ])
            };

            // Aggiungi riga totale
            dataToExport.rows.push([
                'TOTALE',
                totaliTable.oreLavorate,
                totaliTable.oreNonLavorate,
                `${totaliTable.percOreNonLavorate}%`,
                totaliTable.oreDaContratto,
                totaliTable.oreStraordinarie,
                `${totaliTable.percOreStraordinarie}%`
            ]);

            const response = await axios.post(`${API_URL}/operation-HR/downloadExcel`,
                { tableName: selectedCategory || 'Dettaglio Operatori', datiDaStampare: dataToExport },
                { responseType: "blob" }
            );

            if (response.data.type === "application/json") {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Errore durante il download");
            }

            const contentDisposition = response.headers['content-disposition'];
            let fileName = `dettaglio_operatori.xlsx`;

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);
        }
    };

    /* Funzione per effettuare il download del grafico straordinari */
    const handleDownloadChartStraordinari = async () => {
        try {
            const { operatori, oreStraordinarie } = chartDataStraordinari;
            const dataToExport = {
                headers: ['Operatore', 'Ore Straordinarie'],
                rows: operatori.map((op, idx) => [op, oreStraordinarie[idx]])
            };

            const response = await axios.post(`${API_URL}/operation-HR/downloadExcel`,
                { tableName: 'Operatori Straordinari', datiDaStampare: dataToExport },
                { responseType: "blob" }
            );

            if (response.data.type === "application/json") {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Errore durante il download");
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "operatori_straordinari.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);
        }
    };

    /* Funzione per effettuare il download del grafico giustificativi */
    const handleDownloadChartGiustificativi = async () => {
        try {
            const { giustificativi, percentuali } = chartDataGiustificativi;
            const dataToExport = {
                headers: ['Giustificativo', '% Ore Non Lavorate'],
                rows: giustificativi.map((g, idx) => [g, `${percentuali[idx]}%`])
            };

            const response = await axios.post(`${API_URL}/operation-HR/downloadExcel`,
                { tableName: 'Giustificativi Ore Non Lavorate', datiDaStampare: dataToExport },
                { responseType: "blob" }
            );

            if (response.data.type === "application/json") {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Errore durante il download");
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "giustificativi_ore_non_lavorate.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);
        }
    };

    // Calcola altezza dinamica per il grafico straordinari
    const chartHeightStraordinari = useMemo(() => {
        const barHeight = 25;
        const minHeight = 300;
        return Math.max(minHeight, chartDataStraordinari.operatori.length * barHeight + 80);
    }, [chartDataStraordinari.operatori.length]);

    return (
        <div className="dettaglio-oper-container">
            {isDataReady ? (
                <div className="dettaglio-oper-content">
                    {/* KPI Summary Cards */}
                    <div className="oper-hr-kpi-cards dettaglio-oper-kpi">
                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon operatori">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Operatori</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.numOperatori} />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    Media <AnimatedCounter value={kpiSummary.mediaOreOperatore} decimals={1} suffix=" h/op" />
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon lavorate">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Lavorate</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.oreLavorate} decimals={1} suffix=" h" />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    {kpiSummary.oreDaContratto > 0
                                        ? <><AnimatedCounter value={(kpiSummary.oreLavorate / kpiSummary.oreDaContratto) * 100} decimals={1} suffix="%" /> del contratto</>
                                        : 'N/D'
                                    }
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon contratto">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore da Contratto</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.oreDaContratto} decimals={1} suffix=" h" />
                                </span>
                                <span className="oper-hr-kpi-sublabel">Totale periodo</span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card oper-hr-kpi-card-with-ring">
                            <ProgressRing
                                value={kpiSummary.percAssenteismo}
                                size={56}
                                strokeWidth={5}
                                thresholds={{ warning: 5, danger: 15 }}
                                inverse={true}
                                label="Assenteismo"
                            />
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Non Lavorate</span>
                                <span className="oper-hr-kpi-sublabel">
                                    <AnimatedCounter value={kpiSummary.oreNonLavorate} decimals={1} suffix=" h" />
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon straordinarie-pos">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Straordinarie</span>
                                <span className="oper-hr-kpi-value straordinari">
                                    <AnimatedCounter
                                        value={kpiSummary.oreStraordinarie}
                                        decimals={1}
                                        prefix={kpiSummary.oreStraordinarie > 0 ? '+' : ''}
                                        suffix=" h"
                                    />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    {kpiSummary.oreDaContratto > 0
                                        ? <><AnimatedCounter
                                                value={(kpiSummary.oreStraordinarie / kpiSummary.oreDaContratto) * 100}
                                                decimals={1}
                                                prefix={kpiSummary.oreStraordinarie > 0 ? '+' : ''}
                                                suffix="%"
                                            /> vs contratto</>
                                        : 'N/D'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Layout principale: Tabella + Grafici */}
                    <div className="dettaglio-oper-main-content">
                        {/* Parte Sinistra - Tabella */}
                        <div className={`dettaglio-oper-table-section${isFullscreenTable ? ' fullscreen-dettaglio-oper' : ''}`} ref={tableSectionRef}>
                            <RotateDevice active={isFullscreenTable} />
                            <div className="dettaglio-oper-table-header">
                                <div className="dettaglio-oper-table-title-row">
                                    <span className="dettaglio-oper-table-title">Dettaglio Ore Operatori</span>
                                    {(selectedOperatori.length > 0 || selectedGiustificativi.length > 0) && (
                                        <span className="dettaglio-oper-filter-indicator">
                                            (Filtrato)
                                            <button
                                                className="dettaglio-oper-clear-all-btn"
                                                onClick={handleClearAllSelections}
                                                title="Rimuovi tutti i filtri"
                                            >
                                                Resetta filtri
                                            </button>
                                        </span>
                                    )}
                                </div>
                                <div className="dettaglio-oper-table-actions">
                                    <button
                                        className="dettaglio-oper-download-btn"
                                        onClick={handleDownloadFile}
                                        disabled={filteredTableData.length === 0}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        Esporta
                                    </button>
                                    <button
                                        className="dettaglio-oper-fullscreen-btn"
                                        onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                        title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {isFullscreenTable ? (
                                                <>
                                                    <polyline points="4 14 10 14 10 20"></polyline>
                                                    <polyline points="20 10 14 10 14 4"></polyline>
                                                    <line x1="14" y1="10" x2="21" y2="3"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            ) : (
                                                <>
                                                    <polyline points="15 3 21 3 21 9"></polyline>
                                                    <polyline points="9 21 3 21 3 15"></polyline>
                                                    <line x1="21" y1="3" x2="14" y2="10"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            )}
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="dettaglio-oper-table-wrapper">
                                {filteredTableData.length === 0 ? (
                                    <div className="dettaglio-oper-no-data">
                                        <span>Nessun dato trovato per i filtri selezionati</span>
                                    </div>
                                ) : (
                                    <table className="dettaglio-oper-table">
                                        <thead>
                                            <tr>
                                                {[
                                                    { label: 'Operatore', key: 'operatore' },
                                                    { label: 'Ore Lavorate', key: 'oreLavorate' },
                                                    { label: 'Ore Non Lav', key: 'oreNonLavorate' },
                                                    { label: '% Non Lav', key: 'percOreNonLavorate' },
                                                    { label: 'Ore Contratto', key: 'oreDaContratto' },
                                                    { label: 'Ore Straord', key: 'oreStraordinarie' },
                                                    { label: '% Straord', key: 'percOreStraordinarie' },
                                                ].map(({ label, key }) => (
                                                    <th key={key} className="dettaglio-oper-th-sortable" onClick={() => handleSort(key)}>
                                                        {label}
                                                        <span className="dettaglio-oper-sort-icon">
                                                            {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedTableData.map((operatore, index) => (
                                                <tr key={operatore.id || index}>
                                                    <td className="td-operatore">
                                                        <span title={operatore.operatore}>{operatore.operatore}</span>
                                                    </td>
                                                    <td>{formatNumber(operatore.oreLavorate)}</td>
                                                    <td>{formatNumber(operatore.oreNonLavorate)}</td>
                                                    <td className={operatore.percOreNonLavorate > 15 ? 'value-danger' : operatore.percOreNonLavorate > 10 ? 'value-high' : ''}>
                                                        {formatNumber(operatore.percOreNonLavorate)}%
                                                    </td>
                                                    <td>{formatNumber(operatore.oreDaContratto)}</td>
                                                    <td className="value-straordinari">{formatNumber(operatore.oreStraordinarie)}</td>
                                                    <td className="value-straordinari">{formatNumber(operatore.percOreStraordinarie)}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="tr-totali">
                                                <td><strong>TOTALE</strong></td>
                                                <td><strong>{formatNumber(totaliTable.oreLavorate)}</strong></td>
                                                <td><strong>{formatNumber(totaliTable.oreNonLavorate)}</strong></td>
                                                <td className={totaliTable.percOreNonLavorate > 15 ? 'value-danger' : totaliTable.percOreNonLavorate > 10 ? 'value-high' : ''}>
                                                    <strong>{formatNumber(totaliTable.percOreNonLavorate)}%</strong>
                                                </td>
                                                <td><strong>{formatNumber(totaliTable.oreDaContratto)}</strong></td>
                                                <td className="value-straordinari"><strong>{formatNumber(totaliTable.oreStraordinarie)}</strong></td>
                                                <td className="value-straordinari"><strong>{formatNumber(totaliTable.percOreStraordinarie)}%</strong></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>

                        {/* Parte Destra - Grafici */}
                        <div className="dettaglio-oper-chart-section" ref={chartSectionRef}>
                            {/* Grafico Ore Straordinarie */}
                            <div className={`dettaglio-oper-chart-box${isFullscreenChartStraordinari ? ' fullscreen-dettaglio-oper' : ''}`}>
                                <RotateDevice active={isFullscreenChartStraordinari} />
                                <div className="dettaglio-oper-chart-header">
                                    <span className="dettaglio-oper-chart-title">Operatori per Ore Straordinarie</span>
                                    <div className="dettaglio-oper-chart-actions">
                                        {selectedOperatori.length > 0 && (
                                            <button
                                                className="dettaglio-oper-clear-filter-btn"
                                                onClick={handleClearSelectionOperatori}
                                                title="Rimuovi filtro"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                                Rimuovi filtro ({selectedOperatori.length})
                                            </button>
                                        )}
                                        <button
                                            className="dettaglio-oper-download-btn"
                                            onClick={handleDownloadChartStraordinari}
                                            disabled={chartDataStraordinari.operatori.length === 0}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                <polyline points="7 10 12 15 17 10"></polyline>
                                                <line x1="12" y1="15" x2="12" y2="3"></line>
                                            </svg>
                                            Esporta
                                        </button>
                                        <button
                                            className="dettaglio-oper-fullscreen-btn"
                                            onClick={() => setIsFullscreenChartStraordinari(!isFullscreenChartStraordinari)}
                                            title={isFullscreenChartStraordinari ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                {isFullscreenChartStraordinari ? (
                                                    <>
                                                        <polyline points="4 14 10 14 10 20"></polyline>
                                                        <polyline points="20 10 14 10 14 4"></polyline>
                                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                ) : (
                                                    <>
                                                        <polyline points="15 3 21 3 21 9"></polyline>
                                                        <polyline points="9 21 3 21 3 15"></polyline>
                                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Badge filtro attivo */}
                                {selectedOperatori.length > 0 && (
                                    <div className="dettaglio-oper-filter-badge">
                                        <span className="dettaglio-oper-filter-label">Filtrando per:</span>
                                        <div className="dettaglio-oper-filter-tags">
                                            {selectedOperatori.slice(0, 3).map(operatoreId => {
                                                const op = operatoriDataTable.find(o => o.id === operatoreId);
                                                const label = op?.operatore || operatoreId;
                                                return (
                                                    <span key={operatoreId} className="dettaglio-oper-filter-tag" title={label}>
                                                        <span className="dettaglio-oper-filter-tag-text">{label}</span>
                                                        <button
                                                            onClick={() => setSelectedOperatori(prev => prev.filter(id => id !== operatoreId))}
                                                            title="Rimuovi"
                                                        >
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                                            </svg>
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                            {selectedOperatori.length > 3 && (
                                                <span className="dettaglio-oper-filter-more">+{selectedOperatori.length - 3}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {chartDataStraordinari.operatori.length > 0 ? (
                                    <div className="dettaglio-oper-chart-scroll">
                                        <ReactEChartsCore
                                            ref={chartRef}
                                            echarts={echarts}
                                            option={getChartOptionsStraordinari()}
                                            style={{ height: chartHeightStraordinari, width: '100%' }}
                                            onEvents={{ click: handleChartClick }}
                                            lazyUpdate={true}
                                            notMerge={false}
                                        />
                                    </div>
                                ) : (
                                    <div className="dettaglio-oper-empty-chart">
                                        <div className="dettaglio-oper-empty-icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M3 3v18h18"></path>
                                                <path d="M18 9l-5 5-4-4-3 3"></path>
                                            </svg>
                                        </div>
                                        <p>Nessun operatore con ore straordinarie positive</p>
                                    </div>
                                )}
                            </div>

                            {/* Grafico % Ore Non Lavorate per Giustificativo */}
                            <div className={`dettaglio-oper-chart-box${isFullscreenChartGiustificativi ? ' fullscreen-dettaglio-oper' : ''}`}>
                                <RotateDevice active={isFullscreenChartGiustificativi} />
                                <div className="dettaglio-oper-chart-header">
                                    <span className="dettaglio-oper-chart-title">% Ore Non Lavorate su Ore Contratto</span>
                                    <div className="dettaglio-oper-chart-actions">
                                        {selectedGiustificativi.length > 0 && (
                                            <button
                                                className="dettaglio-oper-clear-filter-btn"
                                                onClick={handleClearSelectionGiustificativi}
                                                title="Rimuovi filtro"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                                Rimuovi filtro ({selectedGiustificativi.length})
                                            </button>
                                        )}
                                        <button
                                            className="dettaglio-oper-download-btn"
                                            onClick={handleDownloadChartGiustificativi}
                                            disabled={chartDataGiustificativi.giustificativi.length === 0}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                <polyline points="7 10 12 15 17 10"></polyline>
                                                <line x1="12" y1="15" x2="12" y2="3"></line>
                                            </svg>
                                            Esporta
                                        </button>
                                        <button
                                            className="dettaglio-oper-fullscreen-btn"
                                            onClick={() => setIsFullscreenChartGiustificativi(!isFullscreenChartGiustificativi)}
                                            title={isFullscreenChartGiustificativi ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                {isFullscreenChartGiustificativi ? (
                                                    <>
                                                        <polyline points="4 14 10 14 10 20"></polyline>
                                                        <polyline points="20 10 14 10 14 4"></polyline>
                                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                ) : (
                                                    <>
                                                        <polyline points="15 3 21 3 21 9"></polyline>
                                                        <polyline points="9 21 3 21 3 15"></polyline>
                                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Badge filtro giustificativi attivo */}
                                {selectedGiustificativi.length > 0 && (
                                    <div className="dettaglio-oper-filter-badge dettaglio-oper-filter-badge-blue">
                                        <span className="dettaglio-oper-filter-label">Filtrando per:</span>
                                        <div className="dettaglio-oper-filter-tags">
                                            {selectedGiustificativi.slice(0, 3).map(giust => (
                                                <span key={giust} className="dettaglio-oper-filter-tag dettaglio-oper-filter-tag-blue" title={giust}>
                                                    <span className="dettaglio-oper-filter-tag-text">{giust}</span>
                                                    <button
                                                        onClick={() => setSelectedGiustificativi(prev => prev.filter(g => g !== giust))}
                                                        title="Rimuovi"
                                                    >
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                                        </svg>
                                                    </button>
                                                </span>
                                            ))}
                                            {selectedGiustificativi.length > 3 && (
                                                <span className="dettaglio-oper-filter-more dettaglio-oper-filter-more-blue">+{selectedGiustificativi.length - 3}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {chartDataGiustificativi.giustificativi.length > 0 ? (
                                    <ReactEChartsCore
                                        ref={chartGiustificativiRef}
                                        echarts={echarts}
                                        option={getChartOptionsGiustificativi()}
                                        style={{ height: isMobile ? '180px' : '350px', width: '100%' }}
                                        onEvents={{ click: handleGiustificativoClick }}
                                        lazyUpdate={true}
                                        notMerge={false}
                                    />
                                ) : (
                                    <div className="dettaglio-oper-empty-chart">
                                        <div className="dettaglio-oper-empty-icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M3 3v18h18"></path>
                                                <rect x="7" y="10" width="3" height="8"></rect>
                                                <rect x="14" y="6" width="3" height="12"></rect>
                                            </svg>
                                        </div>
                                        <p>Nessun dato per giustificativi</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="dettaglio-oper-empty-state">
                    <div className="dettaglio-oper-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4>Nessun dato disponibile</h4>
                    <p>Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.</p>
                </div>
            ) : (
                <Lf23Loader />
            )}
        </div>
    );
};

export default DettaglioOper;
